import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { toast } from 'sonner';
import api from '@/lib/api';

const STATUS_STYLES = {
  NOT_STARTED: { bg: 'rgba(100,116,139,0.15)', color: '#64748b', label: 'Not Started' },
  IN_PROGRESS: { bg: 'rgba(59,130,246,0.15)', color: '#3B82F6', label: 'In Progress' },
  ON_TRACK: { bg: 'rgba(16,185,129,0.15)', color: '#10B981', label: 'On Track' },
  AHEAD: { bg: 'rgba(6,182,212,0.15)', color: '#06B6D4', label: 'Ahead' },
  BEHIND: { bg: 'rgba(239,68,68,0.15)', color: '#EF4444', label: 'Behind' },
  COMPLETED: { bg: 'rgba(34,197,94,0.15)', color: '#22C55E', label: 'Completed' },
  SUSPENDED: { bg: 'rgba(245,158,11,0.15)', color: '#F59E0B', label: 'Suspended' },
};

function StatusCellRenderer(params) {
  const status = params.value;
  if (!status) return null;
  const s = STATUS_STYLES[status] || STATUS_STYLES.NOT_STARTED;
  return (
    <span style={{ padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 600, backgroundColor: s.bg, color: s.color, whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  );
}

function ProgressCellRenderer(params) {
  const value = params.value ?? 0;
  const status = params.data?.status;
  const s = STATUS_STYLES[status] || STATUS_STYLES.ON_TRACK;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', width: '100%' }}>
      <div style={{ flex: 1, height: '5px', backgroundColor: 'var(--border-color)', borderRadius: '3px', overflow: 'hidden' }}>
        <div style={{ width: `${Math.min(value, 100)}%`, height: '100%', backgroundColor: s.color, borderRadius: '3px', transition: 'width 0.3s ease' }} />
      </div>
      <span style={{ fontSize: '10px', color: s.color, fontWeight: 600, minWidth: '30px', textAlign: 'right' }}>
        {value.toFixed(1)}%
      </span>
    </div>
  );
}

function PlanQtyCellRenderer(params) {
  const value = params.value;
  if (value == null || value === 0) return <span style={{ color: 'var(--text-disabled)', fontSize: '10px' }}>-</span>;
  return <span style={{ fontSize: '10px', fontFamily: 'monospace', color: '#3B82F6' }}>{value.toFixed(1)}</span>;
}

function ActualQtyCellRenderer(params) {
  const value = params.value;
  if (value == null || value === 0) return <span style={{ color: 'var(--text-disabled)', fontSize: '10px' }}>-</span>;
  return <span style={{ fontSize: '10px', fontFamily: 'monospace', color: '#10B981', fontWeight: 600 }}>{value.toFixed(1)}</span>;
}

function CumPlanPctRenderer(params) {
  const value = params.value;
  if (value == null || value === 0) return <span style={{ color: 'var(--text-disabled)', fontSize: '10px' }}>-</span>;
  return <span style={{ fontSize: '10px', fontFamily: 'monospace', color: '#3B82F6', opacity: 0.7 }}>{value.toFixed(1)}%</span>;
}

function NumberEditor(props) {
  const [value, setValue] = useState(props.value ?? 0);
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); inputRef.current?.select(); }, []);
  const handleChange = (e) => {
    const v = parseFloat(e.target.value) || 0;
    setValue(v);
    if (props.onValueChange) props.onValueChange(v);
  };
  return (
    <input ref={inputRef} type="number" step="0.1" value={value} onChange={handleChange}
      style={{ width: '100%', backgroundColor: 'var(--input-bg)', color: '#10B981',
        border: '1px solid #10B981', borderRadius: '4px', padding: '4px 6px', fontSize: '10px',
        fontFamily: 'monospace', outline: 'none', textAlign: 'right' }} />
  );
}

export default function FCMatrixGrid({ activities, periods, projectId, onRefresh, isDark, granularity = 'daily' }) {
  const gridRef = useRef(null);

  // Group periods by month for monthly view
  const monthlyPeriods = useMemo(() => {
    if (granularity !== 'monthly' || !periods?.length) return [];
    const groups = {};
    periods.forEach(p => {
      const monthKey = p.periodId?.slice(0, 7); // "2025-03"
      if (!groups[monthKey]) groups[monthKey] = { monthKey, periodIds: [], isCurrent: false, isClosed: false };
      groups[monthKey].periodIds.push(p.periodId);
      if (p.isCurrent) groups[monthKey].isCurrent = true;
      if (!p.isClosed) groups[monthKey].isClosed = false;
    });
    return Object.values(groups).sort((a, b) => a.monthKey.localeCompare(b.monthKey));
  }, [periods, granularity]);

  // Build row data with period-based plan & actual quantities
  const rowData = useMemo(() => {
    if (!activities) return [];
    return activities.map((a) => {
      const row = {
        id: a.id,
        activityId: a.activityId,
        activityName: a.activityName,
        disciplineCode: a.disciplineCode,
        activityTypeCode: a.activityTypeCode,
        area: a.area || '',
        scopeQuantity: a.scopeQuantity,
        uom: a.uom,
        totalEquivalentManhours: a.totalEquivalentManhours,
        contributionWeightagePercent: a.contributionWeightagePercent,
        status: a.status,
        overallProgressPct: a.currentProgress?.ftmActualPct || 0,
        _raw: a,
      };

      // Flatten planned distribution (daily)
      (a.plannedDistribution || []).forEach(d => {
        row[`plan_${d.periodId}`] = d.mtdPlanQty;
        row[`cumplan_${d.periodId}`] = d.ftmPlanPct;
      });

      // Flatten actual progress (daily)
      (a.actualProgress || []).forEach(d => {
        row[`actual_${d.periodId}`] = d.mtdActualQty;
      });

      // Monthly aggregated fields
      if (granularity === 'monthly') {
        monthlyPeriods.forEach(mp => {
          let planSum = 0, actualSum = 0, lastCumPlan = 0;
          mp.periodIds.forEach(pid => {
            planSum += row[`plan_${pid}`] || 0;
            actualSum += row[`actual_${pid}`] || 0;
            if (row[`cumplan_${pid}`] != null) lastCumPlan = row[`cumplan_${pid}`];
          });
          row[`mplan_${mp.monthKey}`] = planSum;
          row[`mactual_${mp.monthKey}`] = actualSum;
          row[`mcumplan_${mp.monthKey}`] = lastCumPlan;
        });
      }

      return row;
    });
  }, [activities, granularity, monthlyPeriods]);

  const handleCellValueChanged = useCallback(async (params) => {
    const { data, colDef, newValue } = params;
    const field = colDef.field;
    if (!field || !data._raw) return;

    const match = field.match(/^actual_(.+)$/);
    if (!match) return;
    const periodId = match[1];

    // Hard-guard: refuse writes for future periods, even if the cell was
    // somehow made editable by an out-of-date column config.
    const todayISO = new Date().toISOString().slice(0, 10);
    const periodRow = (periods || []).find(p => p.periodId === periodId);
    const pStart = String(periodRow?.startDate || periodId).slice(0, 10);
    if (pStart > todayISO) {
      toast.error(`Cannot record actual for future date ${periodId}`);
      return;
    }

    try {
      await api.put(`/v1/projects/${projectId}/fc/activities/${data.id}/progress/${periodId}`, {
        mtdActualQty: parseFloat(newValue) || 0
      });
      toast.success(`Updated ${data.activityId} actual for ${periodId}`);
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update');
    }
  }, [projectId, onRefresh]);

  const columnDefs = useMemo(() => {
    const fixedCols = [
      { headerName: '#', valueGetter: (p) => p.node.rowIndex + 1, width: 42, pinned: 'left', sortable: false, filter: false },
      { headerName: 'ID', field: 'activityId', pinned: 'left', width: 85, filter: 'agTextColumnFilter',
        cellStyle: { color: 'var(--accent-primary, #3B82F6)', fontWeight: 600, fontSize: '11px', fontFamily: 'monospace' } },
      { headerName: 'Activity Name', field: 'activityName', pinned: 'left', minWidth: 170, width: 200, filter: 'agTextColumnFilter',
        tooltipField: 'activityName', cellStyle: { fontSize: '11px' } },
      { headerName: 'Disc', field: 'disciplineCode', width: 60, filter: 'agTextColumnFilter', cellStyle: { fontSize: '11px', color: 'var(--text-muted)' } },
      { headerName: 'Type', field: 'activityTypeCode', width: 80, filter: 'agTextColumnFilter', cellStyle: { fontSize: '10px', color: 'var(--text-muted)' } },
      { headerName: 'Area', field: 'area', width: 70, filter: 'agTextColumnFilter', cellStyle: { fontSize: '11px', color: 'var(--text-muted)' } },
      { headerName: 'Scope', field: 'scopeQuantity', width: 75, type: 'numericColumn',
        cellStyle: { fontSize: '11px', fontFamily: 'monospace' },
        valueFormatter: p => p.value?.toLocaleString() || '' },
      { headerName: 'UOM', field: 'uom', width: 55, cellStyle: { fontSize: '10px', color: 'var(--text-muted)' } },
    ];

    let timeCols;

    if (granularity === 'monthly') {
      // Monthly aggregated columns
      timeCols = monthlyPeriods.map(mp => ({
        headerName: mp.monthKey,
        headerClass: mp.isCurrent ? 'ag-header-group-current' : '',
        children: [
          { headerName: 'Plan', field: `mplan_${mp.monthKey}`, width: 70, cellRenderer: PlanQtyCellRenderer, editable: false, type: 'numericColumn' },
          { headerName: 'Actual', field: `mactual_${mp.monthKey}`, width: 75, cellRenderer: ActualQtyCellRenderer, editable: false, type: 'numericColumn' },
          { headerName: 'Cum%', field: `mcumplan_${mp.monthKey}`, width: 60, cellRenderer: CumPlanPctRenderer, editable: false, type: 'numericColumn' },
        ]
      }));
    } else {
      // Daily period columns
      const todayISO = new Date().toISOString().slice(0, 10);
      timeCols = (periods || []).map(p => {
        const pStart = String(p.startDate || p.periodId).slice(0, 10);
        const isFuture = pStart > todayISO;
        return {
          headerName: (p.periodLabel || p.periodId) + (isFuture ? ' 🔒' : ''),
          headerClass: p.isCurrent ? 'ag-header-group-current' : (isFuture ? 'ag-header-group-future' : ''),
          children: [
            { headerName: 'Plan', field: `plan_${p.periodId}`, width: 60, cellRenderer: PlanQtyCellRenderer, editable: false, type: 'numericColumn' },
            { headerName: 'Actual', field: `actual_${p.periodId}`, width: 65, cellRenderer: ActualQtyCellRenderer,
              editable: !p.isClosed && !isFuture,
              cellEditor: NumberEditor, cellEditorPopup: false, type: 'numericColumn',
              tooltipValueGetter: () => isFuture ? 'Future date — actuals are locked until this date has passed' : undefined,
              cellStyle: isFuture ? { backgroundColor: 'rgba(148,163,184,0.08)', cursor: 'not-allowed' } : undefined },
            { headerName: 'Cum%', field: `cumplan_${p.periodId}`, width: 58, cellRenderer: CumPlanPctRenderer, editable: false, type: 'numericColumn' },
          ]
        };
      });
    }

    const summaryCols = [
      { headerName: 'MH', field: 'totalEquivalentManhours', width: 72, type: 'numericColumn',
        cellStyle: { fontSize: '10px', fontFamily: 'monospace' },
        valueFormatter: p => p.value?.toLocaleString() || '' },
      { headerName: 'Wt%', field: 'contributionWeightagePercent', width: 60, type: 'numericColumn',
        cellStyle: { fontSize: '10px', fontFamily: 'monospace' },
        valueFormatter: p => p.value != null ? p.value.toFixed(2) + '%' : '' },
      { headerName: 'Progress', field: 'overallProgressPct', width: 115, cellRenderer: ProgressCellRenderer },
      { headerName: 'Status', field: 'status', width: 90, cellRenderer: StatusCellRenderer },
    ];

    return [...fixedCols, ...timeCols, ...summaryCols];
  }, [periods, granularity, monthlyPeriods]);

  const defaultColDef = useMemo(() => ({ sortable: true, resizable: true, suppressMovable: false, editable: false }), []);
  const gridTheme = isDark ? 'ag-theme-alpine-dark' : 'ag-theme-alpine';

  return (
    <div className={gridTheme} style={{ width: '100%', height: '600px' }} data-testid="fc-matrix-grid">
      <AgGridReact ref={gridRef} rowData={rowData} columnDefs={columnDefs} defaultColDef={defaultColDef}
        onCellValueChanged={handleCellValueChanged} animateRows={true} rowSelection="multiple"
        suppressRowClickSelection={true} enableCellTextSelection={true} tooltipShowDelay={500}
        getRowId={(params) => params.data.id}
        overlayNoRowsTemplate='<span style="color: var(--text-muted); font-size: 13px;">No FC activities found.</span>' />
    </div>
  );
}
